import React, { useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { SignIn } from "./SignIn";
import { EmailVerification } from "./EmailVerification";
import { PhoneNumber } from "./PhoneNumber";
import { PhoneVerification } from "./PhoneVerification";
import { PasswordCreation } from "./PasswordCreation";
import { PasswordLogin } from "./PasswordLogin";
import { ProfileInformation } from "./ProfileInformation";
import { SuccessScreen } from "./SuccessScreen";
import { ForgotPassword } from "./ForgotPassword";
import { ResetPassword } from "./ResetPassword";
import { apiPost } from "../services/api.service";
import { showError, showInfo } from "../services/notification.service";
import { useTranslation } from "../i18n/TranslationContext";

export function AuthFlow() {
	const { currentStep, setCurrentStep, updateAuthData } = useAuth();
	const [isValidatingToken, setIsValidatingToken] = useState(true);
	const { t } = useTranslation();

	useEffect(() => {
		const validateMagicLink = async () => {
			console.log("[AUTH-FLOW] Current pathname:", window.location.pathname);
			console.log("[AUTH-FLOW] Current search:", window.location.search);

			// Get token from URL
			const urlParams = new URLSearchParams(window.location.search);
			const token = urlParams.get("token");
			const email = urlParams.get("email");

			console.log("[AUTH-FLOW] Token:", token ? "present" : "absent");
			console.log("[AUTH-FLOW] Email:", email ? "present" : "absent");

			// Check if this is a password reset link
			// Support both /reset-password and /auth/signin with token (for password reset)
			if (
				token &&
				(window.location.pathname === "/reset-password" ||
					window.location.pathname.includes("/auth/"))
			) {
				// Check if it's a reset token (no email param means it's likely a reset token)
				if (!email) {
					console.log("[AUTH-FLOW] Detected password reset - going to step 9");
					setIsValidatingToken(false);
					setCurrentStep(9); // Reset password step

					// Preserve the token in the URL when on /auth/signin
					if (window.location.pathname !== "/reset-password") {
						const newUrl = `${window.location.pathname}?token=${token}`;
						window.history.replaceState({}, document.title, newUrl);
					}
					return;
				}
			}

			if (!token || !email) {
				// Check if we're on forgot password page
				if (window.location.pathname === "/forgot-password") {
					setCurrentStep(8); // Forgot password step
				}
				// No magic link, proceed normally
				setIsValidatingToken(false);
				return;
			}

			try {
				// Validate the magic link token (disable all notifications)
				const response = await apiPost(
					"/invitations/validate",
					{ token },
					{
						notification: { enabled: false },
					}
				);

				console.log("Validation response:", response);

				// Check if response has nested data structure
				const validationData = response.data || response;

				if (validationData.success && validationData.userData) {
					// Token is valid - store user data
					updateAuthData({
						email: validationData.userData.email,
						firstName: validationData.userData.firstName,
						lastName: validationData.userData.lastName,
						extraId: validationData.userData.extraId,
						phoneNumber: validationData.userData.phoneNumber || "",
					});

					// Store session token
					if (validationData.sessionToken) {
						sessionStorage.setItem(
							"emailVerificationToken",
							validationData.sessionToken
						);
					}

					// Skip to phone number step (skip email verification)
					setCurrentStep(2);
					showInfo(t("auth.invitations.welcome"));

					// Clean URL
					window.history.replaceState(
						{},
						document.title,
						window.location.pathname
					);
				} else {
					// Token invalid or expired - only show the specific message
					const errorMessage =
						validationData.message || t("auth.invitations.invalid");

					// Pre-fill email if available (from backend or URL)
					const emailToPreFill = validationData.email || email;
					if (emailToPreFill) {
						updateAuthData({ email: decodeURIComponent(emailToPreFill) });
					}

					showError(errorMessage);
					setCurrentStep(0); // Go to sign in
				}
			} catch (error: any) {
				console.error("Magic link validation error:", error);
				console.log("Error data:", error?.data);
				console.log("Error email:", error?.data?.email);

				// The email might be directly in error.data
				const errorEmail = error?.data?.email;
				const errorMessage =
					error?.data?.message ||
					error?.message ||
					t("auth.invitations.expired");

				if (errorEmail) {
					console.log("Pre-filling email from error:", errorEmail);
					updateAuthData({ email: errorEmail });
				} else if (email) {
					// Fallback to URL email if available
					console.log("Pre-filling email from URL:", email);
					updateAuthData({ email: decodeURIComponent(email) });
				}

				showError(errorMessage);
				setCurrentStep(0);
			} finally {
				setIsValidatingToken(false);
			}
		};

		validateMagicLink();
	}, []); // Run once on mount

	// Show loading while validating token
	if (isValidatingToken) {
		return (
			<div className="min-h-screen bg-gradient-to-b from-[var(--ucm-bg-gradient-start)] to-[var(--ucm-bg-gradient-end)] flex items-center justify-center p-4">
				<div className="bg-white rounded-3xl shadow-xl p-8 w-full max-w-md text-center">
					<div className="animate-pulse">
						<h2 className="text-xl font-semibold text-[var(--ucm-text-dark)] mb-4">
							{t("auth.invitations.validating")}
						</h2>
						<div className="w-16 h-16 border-4 border-[var(--ucm-primary-teal)] border-t-transparent rounded-full animate-spin mx-auto"></div>
					</div>
				</div>
			</div>
		);
	}

	const renderStep = () => {
		switch (currentStep) {
			case 0:
				return <SignIn />;
			case 1:
				return <EmailVerification />;
			case 2:
				return <PhoneNumber />;
			case 3:
				return <PhoneVerification />;
			case 4:
				return <PasswordCreation />;
			case 5:
				return <ProfileInformation />;
			case 6:
				return <PasswordLogin />; // Password screen for existing users
			case 7:
				return <SuccessScreen />;
			case 8:
				return <ForgotPassword />; // Forgot password screen
			case 9:
				return <ResetPassword />; // Reset password screen
			default:
				return <SignIn />; // Default to Sign In for auth flow
		}
	};

	return renderStep();
}
